import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  differenceInCalendarDays,
  format,
  isToday,
  parseISO,
  startOfDay,
  subDays,
} from "date-fns";
import {
  Building2,
  Loader2,
  RotateCcw,
  ScrollText,
  Search,
  Server,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DateRangePicker,
  type DateRangeValue,
} from "@/components/ui/date-range-picker";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useResourceReinstallReport } from "@/hooks/useResourceQueries";
import {
  CHART_GRID_STROKE,
  CHART_GRID_STROKE_OPACITY,
  CHART_TICK_FILL,
  chartTooltipContentStyle,
} from "@/lib/chartTheme";
import { formatDateRangeForAPI } from "@/lib/date-utils";
import { cn, formatDateTime } from "@/lib/utils";
import { useResourceReinstallFiltersStore } from "@/stores/resourceReinstallFiltersStore";
import {
  RESOURCE_REINSTALL_REPORT_MAX_DAYS,
  VM_PLAN_TYPES,
  type ResourceReinstallReport,
  type ResourceReinstallReportDay,
  type ResourceReinstallReportFilters,
  type ResourceReinstallReportTenant,
  type ResourceReinstallReportTotals,
  type ResourceReinstallReportVm,
  type VmPlanType,
} from "@/types/admin";

/**
 * One colour per plan, the same in the chart, the tiles and both rankings. Blue against the brand
 * pink stays apart under every colour-vision deficiency; the green that marks Paid on the log's
 * chips is a status colour and does not separate from blue for tritan readers.
 */
const PLAN_COLORS: Record<VmPlanType, string> = {
  Trial: "var(--chart-4)",
  Paid: "var(--chart-1)",
};

/** The windows one choice away. Anything else is picked in the date-range control. */
const PRESETS = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
] as const;

/** The period control's value while the window is not one of the presets. */
const CUSTOM_PERIOD = "custom";

const DEFAULT_PRESET_DAYS = 30;

/** Past this many days a bar per day is too thin to read, so the chart adds them up per month. */
const MAX_DAILY_BARS = 62;

/** Up to this many bars each one has room for its number above it. */
const MAX_BARS_WITH_LABELS = 31;

/** "67%" of a total, and "0%" of nothing rather than a division by zero. */
function share(part: number, total: number): string {
  if (total === 0) return "0%";
  return `${Math.round((part / total) * 100)}%`;
}

function plural(count: number, noun: string): string {
  return `${count.toLocaleString()} ${noun}${count === 1 ? "" : "s"}`;
}

/** A window of whole days ending today, as the date-range control holds it. */
function presetRange(days: number): Required<DateRangeValue> {
  const today = startOfDay(new Date());
  return {
    from: subDays(today, days - 1).toISOString(),
    to: today.toISOString(),
  };
}

/**
 * The VM-reinstall report, reached from the sidebar as Resources → "VM Reinstall Report": which
 * VMs and which tenants reinstall most, on which plan, and how that moved over the window. Every
 * figure comes from the rows of the VM Reinstall Log, narrowed by the same search and plan filter.
 *
 * The window is always set and capped at `RESOURCE_REINSTALL_REPORT_MAX_DAYS`: the endpoint
 * aggregates every reinstall inside it and refuses a longer one.
 */
export function ResourceReinstallReportPage() {
  // `range` is what the date control shows, mid-selection included; `appliedRange` is the last
  // complete, allowed window, and the only one the report is read for.
  const [range, setRange] = useState<DateRangeValue>(() =>
    presetRange(DEFAULT_PRESET_DAYS),
  );
  const [appliedRange, setAppliedRange] = useState(() =>
    presetRange(DEFAULT_PRESET_DAYS),
  );
  const [rangeError, setRangeError] = useState<string | null>(null);
  const appliedDays =
    differenceInCalendarDays(
      parseISO(appliedRange.to),
      parseISO(appliedRange.from),
    ) + 1;
  const endsToday = isToday(parseISO(appliedRange.to));
  const isPreset =
    endsToday && PRESETS.some((preset) => preset.days === appliedDays);
  const periodValue = isPreset ? String(appliedDays) : CUSTOM_PERIOD;

  const changeRange = (next: DateRangeValue) => {
    // Cleared: back to the default window, so the control never sits empty over a report that
    // still covers some period it no longer names.
    if (!next.from && !next.to) {
      const fallback = presetRange(DEFAULT_PRESET_DAYS);
      setRange(fallback);
      setAppliedRange(fallback);
      setRangeError(null);
      return;
    }

    setRange(next);

    // One end picked: the selection is not finished, the report keeps its window.
    if (!next.from || !next.to) {
      return;
    }

    const days =
      differenceInCalendarDays(parseISO(next.to), parseISO(next.from)) + 1;
    if (days > RESOURCE_REINSTALL_REPORT_MAX_DAYS) {
      setRangeError(
        `A report covers at most ${RESOURCE_REINSTALL_REPORT_MAX_DAYS} days. Pick a shorter period; the figures below still show the previous one.`,
      );
      return;
    }

    setRangeError(null);
    setAppliedRange({ from: next.from, to: next.to });
  };

  const [vmPlan, setVmPlan] = useState<VmPlanType | "All">("All");

  // The input holds what is being typed; `search` is what the report runs with.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const isSearchPending = searchInput !== search;

  // 750ms, the one debounce every admin list uses.
  useEffect(() => {
    if (searchInput === search) return;

    const timer = setTimeout(() => setSearch(searchInput), 750);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  const filters: ResourceReinstallReportFilters = useMemo(() => {
    const { fromDate, toDate } = formatDateRangeForAPI(
      appliedRange.from,
      appliedRange.to,
      false,
    );

    return {
      search: search.trim() || undefined,
      vmPlan: vmPlan === "All" ? undefined : vmPlan,
      // Both ends are complete ISO dates, so the formatter always returns a value.
      requestedFrom: fromDate ?? appliedRange.from,
      requestedTo: toDate ?? appliedRange.to,
    };
  }, [appliedRange, search, vmPlan]);

  const {
    data: report,
    isFetching,
    error,
    refetch,
  } = useResourceReinstallReport(filters);
  const isLoading = isFetching && !report;

  // Opens the log on the reinstalls behind one line of a ranking: the same period and plan,
  // searched by that line's id. The log reads its filters from its own store on mount.
  const navigate = useNavigate();
  const openLog = (searchTerm: string) => {
    const logFilters = useResourceReinstallFiltersStore.getState();
    logFilters.setFilter("search", searchTerm);
    logFilters.setFilter("vmPlan", vmPlan);
    logFilters.setFilter("dateRange", appliedRange);
    navigate("/admin/resource-reinstalls");
  };

  return (
    <div className="space-y-4 p-4">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold">VM reinstall report</h1>
        <p className="text-sm text-muted-foreground">
          Which VMs and tenants reinstall most, and on which plan. Counted from
          the VM Reinstall Log by the day a reinstall was requested.
        </p>
      </div>

      {/* One row, every control the same height, in the order search · period · plan · dates. The
          date control names the period itself, so nothing below repeats it. */}
      <Card className="shadow-none">
        <CardContent className="space-y-2 p-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-[360px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              {isSearchPending && (
                <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
              )}
              <Input
                placeholder="Search VM name, UUID, ID, IP, email, phone"
                aria-label="Search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                // React withholds onChange while an IME composition is open, so a Vietnamese
                // Telex/VNI term would only reach state once a space closed it.
                onCompositionEnd={(event) =>
                  setSearchInput(event.currentTarget.value)
                }
                className="pl-10 pr-9"
              />
            </div>

            {/* The presets, and "Custom period" as the value shown once the dates beside it were
                picked by hand. */}
            <Select
              value={periodValue}
              onValueChange={(value) => {
                if (value !== CUSTOM_PERIOD) {
                  changeRange(presetRange(Number(value)));
                }
              }}
            >
              <SelectTrigger aria-label="Period" className="w-full sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRESETS.map((preset) => (
                  <SelectItem key={preset.days} value={String(preset.days)}>
                    {preset.label}
                  </SelectItem>
                ))}
                {periodValue === CUSTOM_PERIOD && (
                  <SelectItem value={CUSTOM_PERIOD}>Custom period</SelectItem>
                )}
              </SelectContent>
            </Select>

            <Select
              value={vmPlan}
              onValueChange={(value) => setVmPlan(value as VmPlanType | "All")}
            >
              <SelectTrigger aria-label="VM plan" className="w-full sm:w-40">
                <SelectValue placeholder="VM Plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">Trial and paid</SelectItem>
                {VM_PLAN_TYPES.map((plan) => (
                  <SelectItem key={plan} value={plan}>
                    {plan}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* The control's own trigger is a default-height button; brought to the row's height. */}
            <DateRangePicker
              value={range}
              onChange={changeRange}
              disableFuture
              placeholder="Custom period"
              className="w-full sm:w-64 [&>button]:h-9"
            />

            {isFetching && report && (
              <span
                className="ml-auto flex items-center gap-1.5 pr-1 text-sm text-muted-foreground"
                aria-live="polite"
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Updating…
              </span>
            )}
          </div>

          {rangeError && (
            <p className="px-1 text-sm text-destructive">{rangeError}</p>
          )}
        </CardContent>
      </Card>

      {error && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2 text-sm text-destructive-ink"
        >
          <span>
            {report
              ? "Could not refresh the report. The figures below are the last ones loaded."
              : "Failed to load the report."}{" "}
            {error.message}
          </span>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      )}

      <ReportTotals totals={report?.totals} isLoading={isLoading} />
      <ReinstallsOverTimeCard report={report} isLoading={isLoading} />

      {/* Side by side only where each table keeps room for its columns. */}
      <div className="grid gap-4 2xl:grid-cols-2">
        <TopVmsCard
          vms={report?.topVms}
          isLoading={isLoading}
          onOpenLog={openLog}
        />
        <TopTenantsCard
          tenants={report?.topTenants}
          isLoading={isLoading}
          onOpenLog={openLog}
        />
      </div>
    </div>
  );
}

/** The headline figures: how many reinstalls, on how many VMs and tenants, and the plan split. */
function ReportTotals({
  totals,
  isLoading,
}: {
  totals: ResourceReinstallReportTotals | undefined;
  isLoading: boolean;
}) {
  const total = totals?.total ?? 0;
  const vmCount = totals?.vmCount ?? 0;
  const tenantCount = totals?.tenantCount ?? 0;
  const perVm = vmCount > 0 ? (total / vmCount).toFixed(1) : "0";
  const perTenant = tenantCount > 0 ? (total / tenantCount).toFixed(1) : "0";
  const byPlan: Record<VmPlanType, number> = {
    Trial: totals?.trial ?? 0,
    Paid: totals?.paid ?? 0,
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatTile
        icon={RotateCcw}
        label="Reinstalls"
        value={total}
        caption={`${totals?.completed ?? 0} completed · ${totals?.failed ?? 0} failed · ${totals?.inProgress ?? 0} in progress`}
        isLoading={isLoading}
      />
      <StatTile
        icon={Server}
        label="VMs reinstalled"
        value={vmCount}
        caption={`${perVm} reinstalls per VM on average`}
        isLoading={isLoading}
      />
      <StatTile
        icon={Building2}
        label="Tenants"
        value={tenantCount}
        caption={`${perTenant} reinstalls per tenant on average`}
        isLoading={isLoading}
      />
      <Card className="shadow-none">
        <CardContent className="space-y-2 p-4">
          <p className="text-sm text-muted-foreground">By plan</p>
          {isLoading ? (
            <Skeleton className="h-8 w-32" />
          ) : (
            <>
              <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
                {VM_PLAN_TYPES.map((plan) => (
                  <span key={plan} className="flex items-baseline gap-1.5">
                    <PlanSwatch plan={plan} />
                    <span className="text-sm text-muted-foreground">
                      {plan}
                    </span>
                    <span className="text-2xl font-semibold tabular-nums">
                      {byPlan[plan].toLocaleString()}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {share(byPlan[plan], total)}
                    </span>
                  </span>
                ))}
              </div>
              <PlanBar trial={byPlan.Trial} paid={byPlan.Paid} max={total} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatTile({
  icon: Icon,
  label,
  value,
  caption,
  isLoading,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  caption: string;
  isLoading: boolean;
}) {
  return (
    <Card className="shadow-none">
      <CardContent className="flex items-start gap-3 p-4">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-panel-2 text-ink-2">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 space-y-1">
          <p className="text-sm text-muted-foreground">{label}</p>
          {isLoading ? (
            <Skeleton className="h-8 w-20" />
          ) : (
            <p className="text-2xl font-semibold tabular-nums">
              {value.toLocaleString()}
            </p>
          )}
          <p className="text-xs text-muted-foreground">{caption}</p>
        </div>
      </CardContent>
    </Card>
  );
}

/** The small square that ties a plan's name to its colour wherever the plan is drawn. */
function PlanSwatch({ plan }: { plan: VmPlanType }) {
  return (
    <span
      aria-hidden
      className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
      style={{ backgroundColor: PLAN_COLORS[plan] }}
    />
  );
}

function PlanLegend() {
  return (
    <div className="flex items-center gap-4 text-sm text-muted-foreground">
      {VM_PLAN_TYPES.map((plan) => (
        <span key={plan} className="flex items-center gap-1.5">
          <PlanSwatch plan={plan} />
          {plan}
        </span>
      ))}
    </div>
  );
}

/**
 * A trial/paid split as a bar: as long as the two parts' share of `max`, divided between them.
 * Decorative: the numbers it draws are written beside it.
 */
function PlanBar({
  trial,
  paid,
  max,
}: {
  trial: number;
  paid: number;
  max: number;
}) {
  const share = max > 0 ? ((trial + paid) / max) * 100 : 0;

  return (
    <div aria-hidden className="h-2 w-full rounded-sm bg-panel-2">
      <div className="flex h-2 gap-0.5" style={{ width: `${share}%` }}>
        {trial > 0 && (
          <div
            className="h-full rounded-sm"
            style={{ flexGrow: trial, backgroundColor: PLAN_COLORS.Trial }}
          />
        )}
        {paid > 0 && (
          <div
            className="h-full rounded-sm"
            style={{ flexGrow: paid, backgroundColor: PLAN_COLORS.Paid }}
          />
        )}
      </div>
    </div>
  );
}

interface ChartPoint {
  key: string;
  /** The tick under the bar. */
  label: string;
  /** The days the bar really covers and how many reinstalls it holds, for the tooltip. */
  tooltipLabel: string;
  /** The number drawn above the bar; empty for a bar of zero, which has nothing to label. */
  totalLabel: string;
  Trial: number;
  Paid: number;
}

/**
 * One bar per day, or per month once the window is too long for a bar per day to be read. A
 * month's bar holds only its days inside the window, so its tooltip names those days and not the
 * whole month.
 */
function toChartPoints(days: ResourceReinstallReportDay[]): ChartPoint[] {
  const buckets: {
    key: string;
    label: string;
    firstDate: string;
    lastDate: string;
    trial: number;
    paid: number;
  }[] = [];
  const byMonth = days.length > MAX_DAILY_BARS;

  // The days arrive oldest first, so a month is complete when the next one starts.
  for (const day of days) {
    const key = byMonth ? day.date.slice(0, 7) : day.date;
    let bucket = buckets[buckets.length - 1];
    if (!bucket || bucket.key !== key) {
      bucket = {
        key,
        label: format(parseISO(day.date), byMonth ? "MM/yyyy" : "dd/MM"),
        firstDate: day.date,
        lastDate: day.date,
        trial: 0,
        paid: 0,
      };
      buckets.push(bucket);
    }
    bucket.lastDate = day.date;
    bucket.trial += day.trial;
    bucket.paid += day.paid;
  }

  return buckets.map((bucket) => {
    const total = bucket.trial + bucket.paid;
    const covered =
      bucket.firstDate === bucket.lastDate
        ? format(parseISO(bucket.firstDate), "EEE dd/MM/yyyy")
        : `${format(parseISO(bucket.firstDate), "dd/MM")} – ${format(parseISO(bucket.lastDate), "dd/MM/yyyy")}`;

    return {
      key: bucket.key,
      label: bucket.label,
      tooltipLabel: `${covered} · ${plural(total, "reinstall")}`,
      totalLabel: total > 0 ? total.toLocaleString() : "",
      Trial: bucket.trial,
      Paid: bucket.paid,
    };
  });
}

/** Reinstalls over the window, each bar stacked by plan. */
function ReinstallsOverTimeCard({
  report,
  isLoading,
}: {
  report: ResourceReinstallReport | undefined;
  isLoading: boolean;
}) {
  const points = report ? toChartPoints(report.days) : [];
  const isMonthly = (report?.days.length ?? 0) > MAX_DAILY_BARS;
  const hasReinstalls = (report?.totals.total ?? 0) > 0;
  const showBarLabels = points.length <= MAX_BARS_WITH_LABELS;

  return (
    <Card className="shadow-none">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2 space-y-0 p-4 pb-2">
        <div className="space-y-1">
          <CardTitle className="text-base">
            Reinstalls per {isMonthly ? "month" : "day"}
          </CardTitle>
          <CardDescription>
            By the day a reinstall was requested, in your time zone.
            {isMonthly &&
              " A month at either end of the period holds only its days inside it."}
          </CardDescription>
        </div>
        <PlanLegend />
      </CardHeader>
      <CardContent className="p-4 pt-2">
        {isLoading ? (
          <Skeleton className="h-[300px] w-full" />
        ) : hasReinstalls ? (
          <ResponsiveContainer width="100%" height={300}>
            {/* The top margin is the room for the number above the tallest bar. */}
            <BarChart
              data={points}
              barCategoryGap="20%"
              margin={{ top: 16, right: 8, bottom: 0, left: 0 }}
            >
              <CartesianGrid
                stroke={CHART_GRID_STROKE}
                strokeDasharray="3 3"
                strokeOpacity={CHART_GRID_STROKE_OPACITY}
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={{ fill: CHART_TICK_FILL, fontSize: 12 }}
                stroke={CHART_GRID_STROKE}
                strokeOpacity={CHART_GRID_STROKE_OPACITY}
                minTickGap={16}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: CHART_TICK_FILL, fontSize: 12 }}
                stroke={CHART_GRID_STROKE}
                strokeOpacity={CHART_GRID_STROKE_OPACITY}
                width={36}
              />
              <Tooltip
                cursor={{ fill: CHART_GRID_STROKE, fillOpacity: 0.15 }}
                contentStyle={chartTooltipContentStyle}
                // Values in ink, not in the series colour: the legend ties a plan to its colour.
                itemStyle={{ color: "var(--ink)" }}
                labelFormatter={(label, payload) =>
                  payload?.[0]?.payload?.tooltipLabel ?? label
                }
              />
              {/* The panel-coloured stroke is the gap between the two parts of a bar. */}
              <Bar
                dataKey="Trial"
                stackId="plan"
                fill={PLAN_COLORS.Trial}
                stroke="var(--panel)"
                strokeWidth={1}
                maxBarSize={40}
              />
              <Bar
                dataKey="Paid"
                stackId="plan"
                fill={PLAN_COLORS.Paid}
                stroke="var(--panel)"
                strokeWidth={1}
                radius={[3, 3, 0, 0]}
                maxBarSize={40}
              >
                {/* On the upper part of the stack, so the number sits above the whole bar. */}
                {showBarLabels && (
                  <LabelList
                    dataKey="totalLabel"
                    position="top"
                    fill={CHART_TICK_FILL}
                    fontSize={12}
                  />
                )}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState className="h-[300px]" />
        )}
      </CardContent>
    </Card>
  );
}

/** The VMs reinstalled most often in the window, most first. */
function TopVmsCard({
  vms,
  isLoading,
  onOpenLog,
}: {
  vms: ResourceReinstallReportVm[] | undefined;
  isLoading: boolean;
  onOpenLog: (searchTerm: string) => void;
}) {
  const rows = vms ?? [];

  return (
    <Card className="shadow-none">
      <CardHeader className="space-y-1 p-4 pb-2">
        <CardTitle className="text-base">Most reinstalled VMs</CardTitle>
        <CardDescription>
          Up to 10 VMs, most reinstalls in the period first, whatever their
          outcome.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 pt-2">
        {isLoading ? (
          <RankingSkeleton />
        ) : rows.length === 0 ? (
          <EmptyState className="h-40" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>VM</TableHead>
                <TableHead className="text-right">Trial</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Last reinstall</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((vm, index) => (
                // Every field the ranking groups a VM by: a renamed or transferred VM is two rows.
                <TableRow
                  key={`${vm.resourceId}|${vm.providerVmUuid}|${vm.vmName}|${vm.tenantId}`}
                >
                  <TableCell className="text-muted-foreground">
                    {index + 1}
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/admin/resources/${vm.resourceId}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {vm.vmName}
                    </Link>
                    <p className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                      UUID: {vm.providerVmUuid}
                    </p>
                    {vm.failed > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {vm.failed} failed
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {vm.trial}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {vm.paid}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    {vm.total}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatDateTime(vm.lastRequestedAt)}
                  </TableCell>
                  <TableCell>
                    <OpenLogButton
                      name={vm.vmName}
                      onClick={() => onOpenLog(vm.resourceId)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/** The tenants with the most reinstalls in the window, most first. */
function TopTenantsCard({
  tenants,
  isLoading,
  onOpenLog,
}: {
  tenants: ResourceReinstallReportTenant[] | undefined;
  isLoading: boolean;
  onOpenLog: (searchTerm: string) => void;
}) {
  const rows = tenants ?? [];

  return (
    <Card className="shadow-none">
      <CardHeader className="space-y-1 p-4 pb-2">
        <CardTitle className="text-base">
          Tenants with the most reinstalls
        </CardTitle>
        <CardDescription>
          Up to 10 tenants, most reinstalls in the period first, across all
          their VMs.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 pt-2">
        {isLoading ? (
          <RankingSkeleton />
        ) : rows.length === 0 ? (
          <EmptyState className="h-40" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead className="text-right">VMs</TableHead>
                <TableHead className="text-right">Trial</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((tenant, index) => (
                <TableRow key={tenant.tenantId}>
                  <TableCell className="text-muted-foreground">
                    {index + 1}
                  </TableCell>
                  <TableCell>
                    <TenantLink id={tenant.tenantId} name={tenant.tenantName} />
                    {tenant.ownerEmail && (
                      <p className="text-xs text-muted-foreground">
                        {tenant.ownerEmail}
                      </p>
                    )}
                    {tenant.ownerPhoneNumber && (
                      <p className="text-xs text-muted-foreground">
                        {tenant.ownerPhoneNumber}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {tenant.vmCount}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {tenant.trial}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {tenant.paid}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    {tenant.total}
                  </TableCell>
                  <TableCell>
                    <OpenLogButton
                      name={tenant.tenantName ?? tenant.tenantId}
                      onClick={() => onOpenLog(tenant.tenantId)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/** Opens the reinstall log on the reinstalls behind one row of a ranking. */
function OpenLogButton({
  name,
  onClick,
}: {
  name: string;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-8 w-8 hover:translate-y-0 active:translate-y-0"
      title="View these reinstalls in the log"
      aria-label={`View the reinstalls of ${name} in the log`}
      onClick={onClick}
    >
      <ScrollText className="h-4 w-4" />
    </Button>
  );
}

/** The tenant's name linking to the tenant, or its id when the tenant no longer exists. */
function TenantLink({ id, name }: { id: string; name: string | null }) {
  if (!name) {
    return (
      <span className="font-mono text-xs text-muted-foreground">{id}</span>
    );
  }

  return (
    <Link to={`/admin/tenants/${id}`} className="hover:underline">
      {name}
    </Link>
  );
}

function RankingSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }, (_, index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </div>
  );
}

function EmptyState({ className }: { className: string }) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2 text-center text-muted-foreground",
        className,
      )}
    >
      <RotateCcw className="h-8 w-8" />
      <p className="text-sm">No reinstalls in this period</p>
    </div>
  );
}
